import Button from '@components/UI/Button';
import Field, { FieldFeedback } from '@components/UI/Field';
import Input from '@components/UI/Input';

import useMods from '@hooks/useMods';
import RenderIf from '@utils/renderIf';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import FileUploaderDrag from './FileUploaderDrag';
import FileUploaderFile from './FileUploaderFile';
import FileUploaderMessage from './FileUploaderMessage';

const FileUploader = ({ mods, id, errors, filesUpload }) => {
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState({});

    const dropRef = useRef(null);
    const dragRef = useRef(null);

    const { className } = useMods(FileUploader, { mods: { ...mods, ...{ invalid: Object.prototype.hasOwnProperty.call(errors, 'purchaseFile') }, dragging } });

    const drop = (file) => {
        setFiles(file);

        filesUpload(file);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target !== dragRef.current)
            setDragging(true);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const { files } = event.dataTransfer;

        if (files?.length)
            drop(files);

        setDragging(false);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.target === dragRef.current)
            setDragging(false);
    };

    useEffect(() => {
        const drop = dropRef.current;

        drop.addEventListener('dragover', handleDragOver);
        drop.addEventListener('dragenter', handleDragEnter);
        drop.addEventListener('drop', handleDrop);
        drop.addEventListener('dragleave', handleDragLeave);

        return () => {
            drop.removeEventListener('dragover', handleDragOver);
            drop.removeEventListener('dragenter', handleDragEnter);
            drop.removeEventListener('drop', handleDrop);
            drop.removeEventListener('dragleave', handleDragLeave);
        };
    }, []);

    return (
        <>
            <div
                className={ className }
                ref={ dropRef }
            >
                <RenderIf
                    isTrue={ dragging }
                >
                    <FileUploaderDrag
                        ref={ dragRef }
                    />
                </RenderIf>

                <FileUploaderMessage />

                <Field
                    mods={ { 'no-spacing': true } }
                >
                    <Input
                        type='file'
                        id={ id }
                        mods={ { hidden: true } }
                        onChange={ (event) => drop(event.target.files) }
                    />

                    <Button
                        type=''
                        tag='label'
                        variant='outline_primary'
                        color='neutral'
                        size='sm'
                        buttonProps={ { htmlFor: id } }
                    >
                        <span className='hidden md:block'>or browse to choose a file</span>
                        <span className='block md:hidden'>+ Add file</span>
                    </Button>
                </Field>
            </div>

            <RenderIf
                isTrue={ errors.purchaseFile }
            >
                <FieldFeedback
                    invalid
                >
                    { errors.purchaseFile?.join('\n') }
                </FieldFeedback>
            </RenderIf>

            { !!files?.length && (
                <FileUploaderFile
                    file={ files[0] }
                    name={ files[0].name }
                    onClick={ () => {
                        setFiles(false);

                        document.getElementById(id).value = '';
                    } }
                />
            ) }
        </>
    );
};

FileUploader.displayName = 'File-Uploader';

FileUploader.propTypes = {
    errors: PropTypes.object,
    filesUpload: PropTypes.func,
    id: PropTypes.string,
    mods: PropTypes.object
};

export default FileUploader;
